import React from 'react';
import { ActionItem, ActionToolbarVariant } from '../types';
import { BaseActionButtonProps } from '../ActionButtons/BaseActionButtonProps';
import { PlaceholderButton } from '../ActionButtons/PlaceholderButton/PlaceholderButton';
import { JumpToRecipeButton } from '../ActionButtons/JumpToRecipeButton';
import { ShareButton } from '../ActionButtons/ShareButton';
import { ShopButton } from '../ActionButtons/ShopButton';
import { EmailButton } from '../ActionButtons/EmailButton';
import { PinButton } from '../ActionButtons/PinButton';
import { FacebookButton } from '../ActionButtons/FacebookButton';
import { KeepScreenOnToggle } from '../ActionButtons/KeepScreenOnToggle';
import { PrintButton } from '../ActionButtons/PrintButton';
import { SaveButton } from '../ActionButtons/SaveButton';

/**
 * Props for the useActionRenderer hook
 */
export interface UseActionRendererProps {
  variant: ActionToolbarVariant;
  toolbarIdentifier?: string;
}

/**
 * Custom hook that provides action rendering functionality
 * Encapsulates the logic for mapping action types to their corresponding components
 *
 * @param props - Configuration for the action renderer
 * @returns Object containing the renderActionComponent and renderActionList functions
 */
export function useActionRenderer({ variant, toolbarIdentifier }: UseActionRendererProps): {
  renderActionComponent: (
    action: ActionItem,
    filledPlaceholder?: boolean,
    isInSubActions?: boolean
  ) => JSX.Element | JSX.Element[];
  renderActionList: (
    actions: ActionItem[],
    maxActions?: number,
    className?: string,
    isInSubActions?: boolean
  ) => JSX.Element | JSX.Element[];
} {
  /**
   * Renders an action component based on the action type
   *
   * @param action - The action item to render
   * @param filledPlaceholder - Whether to render a filled placeholder for missing actions
   * @returns JSX element representing the action component
   */
  const renderActionComponent = React.useCallback(
    (
      action: ActionItem,
      filledPlaceholder = false,
      isInSubActions = false
    ): JSX.Element | JSX.Element[] => {
      const commonProps: BaseActionButtonProps = {
        label: action.label || '',
        variant,
        eventPositionSuffix: action.eventPositionSuffix,
        toolbarIdentifier,
      };

      // If type is not provided, render placeholder button
      if (!action.type) {
        return <PlaceholderButton {...commonProps} filledPlaceholder={filledPlaceholder} />;
      }

      // Map action types to their corresponding components
      switch (action.type) {
        case 'Save':
          return <SaveButton {...commonProps} isSubAction={isInSubActions} />;
        case 'Pinterest':
          return <PinButton {...commonProps} isSubAction={isInSubActions} />;
        case 'Print':
          return <PrintButton {...commonProps} isSubAction={isInSubActions} />;
        case 'Share':
          return (
            <ShareButton
              {...commonProps}
              dialogPosition={variant === 'vertical' ? 'right' : 'bottom'}
              subActions={action.actionButtons}
            />
          );
        case 'Shop':
          return <ShopButton {...commonProps} isSubAction={isInSubActions} />;
        case 'Keep Toggle On':
          return <KeepScreenOnToggle {...commonProps} isSubAction={isInSubActions} />;
        case 'Jump to':
          return <JumpToRecipeButton {...commonProps} isSubAction={isInSubActions} />;
        case 'Email':
          return <EmailButton {...commonProps} isSubAction={isInSubActions} />;
        case 'Facebook':
          return <FacebookButton {...commonProps} isSubAction={isInSubActions} />;
        // case 'X':
        //   return <XButton {...commonProps} />;
        default:
          return <PlaceholderButton {...commonProps} filledPlaceholder={filledPlaceholder} />;
      }
    },
    [variant, toolbarIdentifier]
  );

  /**
   * Renders a list of actions
   *
   * @param actions - Array of action items to render
   * @param maxActions - Maximum number of actions to render (default: 5)
   * @param className - Optional CSS class name for the container
   * @returns JSX element containing the rendered actions
   */
  const renderActionList = React.useCallback(
    (actions: ActionItem[], maxActions = 5, className?: string, isInSubActions = false) => {
      if (!actions || actions.length === 0) {
        return <></>;
      }

      return actions.slice(0, maxActions).map((action, index) => (
        <li key={`${action.label}-${index}`} className={className}>
          {renderActionComponent(action, index === 0, isInSubActions)}
        </li>
      ));
    },
    [renderActionComponent]
  );

  return {
    renderActionComponent,
    renderActionList,
  };
}
